import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Checkbox,
    Confirm,
    Form,
    FormField,
    Message,
    TextArea,
} from 'semantic-ui-react';
import { LoadingWrapper } from '../../common';
import { fetchWithAuth, getCSRFToken, getUsername, isICCSupport } from '../../auth';
import { formatDateToMmDdYyyy } from '../../../utils/formatDate';
import {
    answerQuestion,
    createQuestion,
    deleteQuestion,
    fetchAnswerers,
    fetchQuestion,
    updateQuestion,
} from './questionFormService';

const ANONYMOUS_MESSAGE = 'If you select anonymous, the question will be addressed in a group setting, email, or other avenue that will include associates within Internal Constructions.';

function canAnswerQuestion (answerers) {
    if (isICCSupport()) {
        return true;
    }
    const username = getUsername();
    return answerers.some((answerer) => answerer.userId === username);
}

export const QuestionFormDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === undefined || id === 'new';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [associateName, setAssociateName] = useState('');
    const [answerers, setAnswerers] = useState([]);
    const [formData, setFormData] = useState({
        anonymous: false,
        question: '',
        targetRespondent: '',
    });
    const [answerText, setAnswerText] = useState('');
    const [existingForm, setExistingForm] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const answererOptions = useMemo(() => (
        answerers.map((answerer) => ({
            key: answerer.userId,
            value: answerer.userId,
            text: `${answerer.name} (${answerer.role})`,
        }))
    ), [answerers]);

    const isOwner = !isNew
        && existingForm?.submittedBy
        && existingForm.submittedBy === getUsername();
    const canManage = !isNew && isICCSupport();
    const canEditQuestion = isNew
        || canManage
        || (isOwner && existingForm?.status === 'open');
    const canSubmitAnswer = !isNew
        && existingForm?.status === 'open'
        && canAnswerQuestion(answerers);

    useEffect(() => {
        async function loadForm () {
            setLoaded(false);
            setError('');
            try {
                const answererList = await fetchAnswerers();
                setAnswerers(answererList);

                if (isNew) {
                    const memberResp = await fetchWithAuth(
                        `${process.env.REACT_APP_ICC_TOOL_API_HOST}members/${getUsername()}/`,
                        {
                            headers: { 'X-CSRFToken': getCSRFToken() },
                            credentials: 'include',
                        }
                    );
                    if (memberResp.ok) {
                        const memberData = await memberResp.json();
                        setAssociateName(memberData.name || getUsername());
                    } else {
                        setAssociateName(getUsername());
                    }
                    setExistingForm(null);
                    setFormData({ anonymous: false, question: '', targetRespondent: '' });
                    setAnswerText('');
                } else {
                    const detail = await fetchQuestion(id);
                    setExistingForm(detail);
                    setAssociateName(detail.submittedByName);
                    setFormData({
                        anonymous: detail.anonymous,
                        question: detail.question,
                        targetRespondent: detail.targetRespondent,
                    });
                    setAnswerText(detail.answer || '');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoaded(true);
            }
        }

        loadForm();
    }, [id, isNew]);

    async function handleSubmit () {
        setError('');
        setSuccess('');
        if (!formData.question.trim()) {
            setError('Question is required.');
            return;
        }
        if (!formData.targetRespondent) {
            setError('Target respondent is required.');
            return;
        }

        setLoaded(false);
        try {
            const created = await createQuestion({
                anonymous: formData.anonymous,
                question: formData.question.trim(),
                targetRespondent: formData.targetRespondent,
            });
            setSuccess('Question submitted successfully.');
            navigate(`/forms/questions/${created.id}`, { replace: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }

    async function handleSave () {
        setError('');
        setSuccess('');
        if (!formData.question.trim()) {
            setError('Question is required.');
            return;
        }
        if (!formData.targetRespondent) {
            setError('Target respondent is required.');
            return;
        }

        setLoaded(false);
        try {
            const updated = await updateQuestion(id, {
                anonymous: formData.anonymous,
                question: formData.question.trim(),
                targetRespondent: formData.targetRespondent,
            });
            setExistingForm(updated);
            setAssociateName(updated.submittedByName);
            setFormData({
                anonymous: updated.anonymous,
                question: updated.question,
                targetRespondent: updated.targetRespondent,
            });
            setSuccess('Question form updated.');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }

    async function handleAnswer () {
        setError('');
        setSuccess('');
        if (!answerText.trim()) {
            setError('Answer is required.');
            return;
        }
        setLoaded(false);
        try {
            const updated = await answerQuestion(id, answerText.trim());
            setExistingForm(updated);
            setAnswerText(updated.answer || '');
            setSuccess('Question answered and closed.');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }

    async function handleDelete () {
        setConfirmDelete(false);
        setError('');
        setSuccess('');
        setLoaded(false);
        try {
            await deleteQuestion(id);
            navigate('/forms/questions', { replace: true });
        } catch (err) {
            setError(err.message);
            setLoaded(true);
        }
    }

    const displaySubmittedDate = existingForm?.submittedDate
        ? formatDateToMmDdYyyy(existingForm.submittedDate)
        : '';
    const displayClosedDate = existingForm?.closedDate
        ? formatDateToMmDdYyyy(existingForm.closedDate)
        : '';

    return (
        <LoadingWrapper loading={!loaded}>
            <div style={{ maxWidth: '40rem', margin: '0 auto' }}>
                {success && <Message positive>{success}</Message>}
                {error && <Message negative>{error}</Message>}
                {canEditQuestion && answererOptions.length === 0 && (
                    <Message warning>
                        No respondents available. Set ICC_TOOL_QUESTIONS_FORM_ANSWERER_USER_IDS,
                        or ensure active Managers, Directors, or VPs exist locally.
                    </Message>
                )}
                <Form>
                    <FormField>
                        <label>Associate Name</label>
                        <Form.Input value={associateName} readOnly className="icct-disabled-input" />
                    </FormField>
                    {canEditQuestion && (
                        <FormField>
                            <Checkbox
                                label="Remain anonymous"
                                checked={formData.anonymous}
                                onChange={(e, { checked }) => setFormData((current) => ({
                                    ...current,
                                    anonymous: checked,
                                }))}
                            />
                            {formData.anonymous && (
                                <Message info style={{ marginTop: '1rem' }}>{ANONYMOUS_MESSAGE}</Message>
                            )}
                        </FormField>
                    )}
                    {!canEditQuestion && existingForm?.anonymous && (
                        <Message info>{ANONYMOUS_MESSAGE}</Message>
                    )}
                    <FormField required={canEditQuestion}>
                        <label>Question</label>
                        <TextArea
                            value={formData.question}
                            readOnly={!canEditQuestion}
                            onChange={(e, { value }) => setFormData((current) => ({
                                ...current,
                                question: value,
                            }))}
                        />
                    </FormField>
                    <FormField>
                        <label>Submitted Date</label>
                        <Form.Input value={displaySubmittedDate} readOnly className="icct-disabled-input" />
                    </FormField>
                    <FormField required={canEditQuestion}>
                        <label>Target Respondent</label>
                        {canEditQuestion
                            ? (
                                <Form.Select
                                    placeholder="Select who should answer this question"
                                    options={answererOptions}
                                    value={formData.targetRespondent}
                                    search
                                    selection
                                    disabled={answererOptions.length === 0}
                                    onChange={(e, { value }) => setFormData((current) => ({
                                        ...current,
                                        targetRespondent: value,
                                    }))}
                                />
                            )
                            : (
                                <Form.Input
                                    value={existingForm?.targetRespondentName || ''}
                                    readOnly
                                    className="icct-disabled-input"
                                />
                            )}
                    </FormField>
                    {!isNew && (
                        <>
                            <FormField>
                                <label>Status</label>
                                <Form.Input
                                    value={existingForm?.statusDisplay || ''}
                                    readOnly
                                    className="icct-disabled-input"
                                />
                            </FormField>
                            <FormField required={canSubmitAnswer}>
                                <label>Answer</label>
                                <TextArea
                                    value={answerText}
                                    readOnly={!canSubmitAnswer}
                                    onChange={(e, { value }) => setAnswerText(value)}
                                />
                            </FormField>
                            <FormField>
                                <label>Answered By</label>
                                <Form.Input
                                    value={existingForm?.answeredByName || '—'}
                                    readOnly
                                    className="icct-disabled-input"
                                />
                            </FormField>
                            <FormField>
                                <label>Closed Date</label>
                                <Form.Input
                                    value={displayClosedDate}
                                    readOnly
                                    className="icct-disabled-input"
                                />
                            </FormField>
                            {existingForm?.editedByName && (
                                <FormField>
                                    <label>Edited By</label>
                                    <Form.Input
                                        value={existingForm.editedByName}
                                        readOnly
                                        className="icct-disabled-input"
                                    />
                                </FormField>
                            )}
                        </>
                    )}
                    {isNew && (
                        <Button
                            style={{ backgroundColor: 'var(--add)', color: 'white' }}
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>
                    )}
                    {!isNew && canEditQuestion && (
                        <Button
                            style={{ backgroundColor: 'var(--add)', color: 'white' }}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    )}
                    {canSubmitAnswer && (
                        <Button
                            style={{ backgroundColor: 'var(--add)', color: 'white', marginLeft: '0.5rem' }}
                            onClick={handleAnswer}
                        >
                            Submit Answer
                        </Button>
                    )}
                    {canManage && (
                        <Button
                            style={{ backgroundColor: 'var(--delete)', color: 'white', marginLeft: '0.5rem' }}
                            onClick={() => setConfirmDelete(true)}
                        >
                            Delete
                        </Button>
                    )}
                </Form>
                <Confirm
                    open={confirmDelete}
                    header="Delete question form"
                    content="Delete this question form? This cannot be undone."
                    confirmButton="Delete"
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={handleDelete}
                />
            </div>
        </LoadingWrapper>
    );
};
